import type { PanelModel, PanelRow } from "@easynutrifact/label-engine";
// Shared, browser-safe SVG primitives (geometry + esc/num/text/rule + the SVG document wrapper).
// These import only `./font-family` (a zero-import constant), never `./fonts` (which pulls
// `node:url` — banned from the browser bundle that the live preview loads via `./client`).
import {
  BAR_3,
  BAR_7,
  HAIRLINE,
  PAD,
  X_LEFT,
  X_RIGHT,
  rule,
  text,
  wrapPanelSvg,
  wrapText,
} from "./svg-primitives";

/** The supported label layouts. */
export type LabelFormat = "vertical-us-nf" | "vertical-us-sf";

// ---------------------------------------------------------------------------
// FDA typography constants (21 CFR 101.9(d) + COMPLIANCE-ENGINE-SPEC §1.6).
// All sizes/positions in POINTS (1pt = 1/72in). Values are static constants — the
// renderer never reads the clock or RNG, so identical input -> byte-identical SVG.
// ---------------------------------------------------------------------------

// Type sizes (points). Calories number must be >=22pt; nutrient names >=8pt; footnote >=6pt.
const FS_TITLE = 17;
const FS_SERVINGS = 8.5;
const FS_SERVING_SIZE = 9;
const FS_AMOUNT_HEADER = 8;
const FS_CALORIES_LABEL = 13;
const FS_CALORIES_NUM = 28;
const FS_DV_HEADER = 8;
const FS_NUTRIENT = 8;
const FS_PDV = 8;
const FS_FOOTNOTE = 6.5;

// Vertical rhythm.
const LINE_GAP = 3; // gap between stacked text lines
const ROW_H = 11.5; // height of one nutrient row (>= name size + breathing room)
const INDENT_STEP = 9; // x-offset per indent level

/**
 * Render a `PanelModel` to a deterministic SVG string. The SAME function runs in the browser
 * (admin live preview, injected as inline `<svg>`) and the Node export worker (→ resvg → PNG),
 * so preview == export by construction. Pure & deterministic: no `Date`, no `Math.random`.
 */
export function renderLabelSVG(panel: PanelModel, format: LabelFormat): string {
  if (format !== "vertical-us-nf") {
    throw new Error(`label format ${format} not implemented (M1 = vertical-us-nf only)`);
  }

  const body: string[] = [];
  let y = PAD; // running vertical cursor (baseline-aware below)

  // --- Title: "Nutrition Facts", largest + bold. ---
  y += FS_TITLE;
  body.push(text({ x: X_LEFT, y, size: FS_TITLE, text: panel.title, bold: true }));

  // --- Servings per container + serving size (small). ---
  y += FS_SERVINGS + LINE_GAP;
  body.push(text({ x: X_LEFT, y, size: FS_SERVINGS, text: panel.servingsLine }));
  y += FS_SERVING_SIZE + LINE_GAP;
  body.push(
    text({ x: X_LEFT, y, size: FS_SERVING_SIZE, text: panel.servingLine, bold: true }),
  );

  // --- 7pt bar under the serving header (the "Amount per serving" rule). ---
  y += LINE_GAP + BAR_7 / 2;
  body.push(rule(y, BAR_7));
  y += BAR_7 / 2;

  // --- "Amount per serving" header row + "% Daily Value*" header. ---
  y += FS_AMOUNT_HEADER + LINE_GAP;
  body.push(
    text({ x: X_LEFT, y, size: FS_AMOUNT_HEADER, text: "Amount per serving", bold: true }),
  );
  body.push(
    text({ x: X_RIGHT, y, size: FS_DV_HEADER, text: "% Daily Value*", bold: true, anchor: "end" }),
  );

  // --- Calories row: label + big bold number. ---
  y += LINE_GAP + HAIRLINE;
  body.push(rule(y, HAIRLINE));
  y += FS_CALORIES_NUM; // baseline for the big number
  body.push(text({ x: X_LEFT, y, size: FS_CALORIES_LABEL, text: "Calories", bold: true }));
  body.push(
    text({ x: X_RIGHT, y, size: FS_CALORIES_NUM, text: panel.calories, bold: true, anchor: "end" }),
  );

  // --- 3pt section bar under Calories. ---
  y += LINE_GAP + BAR_3 / 2;
  body.push(rule(y, BAR_3));
  y += BAR_3 / 2;

  // --- Nutrient rows. ---
  for (let i = 0; i < panel.rows.length; i++) {
    const row = panel.rows[i] as PanelRow;

    // Hairline above every nutrient row (skip before the first — the 3pt bar sits there).
    if (i > 0) {
      y += HAIRLINE;
      body.push(rule(y, HAIRLINE));
    }

    const indentX = X_LEFT + row.indent * INDENT_STEP;
    y += ROW_H - LINE_GAP; // baseline within the row band

    // Label + (for non-added-sugars rows) the amount appended after the bold name.
    // The added-sugars row bakes its amount into the label ("Includes 10g Added Sugars").
    const labelStr = row.amountStr ? `${row.label} ${row.amountStr}` : row.label;
    body.push(
      text({ x: indentX, y, size: FS_NUTRIENT, text: labelStr, bold: row.bold }),
    );

    if (row.pdvStr) {
      body.push(
        text({ x: X_RIGHT, y, size: FS_PDV, text: row.pdvStr, bold: true, anchor: "end" }),
      );
    }
    y += LINE_GAP;
  }

  // --- 3pt section bar above the footnotes. ---
  y += LINE_GAP + BAR_3 / 2;
  body.push(rule(y, BAR_3));
  y += BAR_3 / 2;

  // --- Footnotes (>= 6pt). Word-wrap each note to the inner panel width so long free-text
  //     (e.g. the mandatory "*The % Daily Value..." line) stacks instead of overflowing/clipping. ---
  for (const note of panel.footnotes) {
    for (const line of wrapText(note, FS_FOOTNOTE, X_RIGHT - X_LEFT)) {
      y += FS_FOOTNOTE + LINE_GAP;
      body.push(text({ x: X_LEFT, y, size: FS_FOOTNOTE, text: line }));
    }
  }

  // --- Finalize height + heavy border box. ---
  y += PAD;
  return wrapPanelSvg(y, body.join(""));
}
